import React, { useState, useRef } from 'react';
import { nanoid } from 'nanoid';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy, rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { ContentBlock } from '@/store/api/sessionsApi';
import { useUploadFileMutation } from '@/store/api/storageApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import {
  GripVertical,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
  Type,
  List,
  AlignLeft,
  Image as ImageIcon,
  Link2,
  Code,
  Eye,
  Upload,
  Globe,
  Loader2,
  Play,
  HelpCircle,
  MessageSquare,
  Terminal,
  Clock,
  Quote,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';

const getUrlWarning = (url: string) => {
  if (!url) return null;
  if (url.startsWith('/')) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('prnt.sc'))
      return 'Lightshot (prnt.sc) URLs are webpages, not images. Right-click the image and select "Copy image address".';
    if (u.hostname.includes('drive.google.com'))
      return 'Google Drive links cannot be embedded directly as images.';
    if (u.hostname.includes('dropbox.com') && !url.includes('raw=1'))
      return 'Dropbox links must end with ?raw=1 to work as images.';
    const ext = u.pathname.split('.').pop()?.toLowerCase() || '';
    if (['html', 'php', 'aspx'].includes(ext))
      return 'This looks like a webpage link, not a direct image file.';
    return null;
  } catch {
    return null;
  }
};

const blockTypes = [
  { id: 'overview', icon: <Type size={16} />, label: 'Overview', desc: 'Short summary text' },
  { id: 'deep-dive', icon: <AlignLeft size={16} />, label: 'Deep Dive', desc: 'Long form content' },
  {
    id: 'learning-points',
    icon: <List size={16} />,
    label: 'Learning Points',
    desc: 'Checklist of outcomes',
  },
  {
    id: 'gallery',
    icon: <ImageIcon size={16} />,
    label: 'Gallery',
    desc: 'Images from the session',
  },
  {
    id: 'resources',
    icon: <Link2 size={16} />,
    label: 'Resources',
    desc: 'External links & files',
  },
  { id: 'markdown', icon: <Code size={16} />, label: 'Markdown', desc: 'Rich text content' },
  {
    id: 'custom',
    icon: <Type size={16} />,
    label: 'Custom Section',
    desc: 'Create your own heading & content',
  },
  {
    id: 'faq',
    icon: <HelpCircle size={16} />,
    label: 'FAQ / Accordion',
    desc: 'Q&A format sections',
  },
  {
    id: 'callout',
    icon: <MessageSquare size={16} />,
    label: 'Callout / Quote',
    desc: 'Highlighted alerts or quotes',
  },
  {
    id: 'code',
    icon: <Terminal size={16} />,
    label: 'Code Snippet',
    desc: 'Syntax highlighted code',
  },
  {
    id: 'timeline',
    icon: <Clock size={16} />,
    label: 'Timeline / Steps',
    desc: 'Sequential steps layout',
  },
] as const;

interface ContentBlocksEditorProps {
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
}

export function ContentBlocksEditor({ blocks, onChange }: ContentBlocksEditorProps) {
  const [showAddMenu, setShowAddMenu] = useState<number | null>(null);
  const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});
  const [blockToDelete, setBlockToDelete] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      onChange(arrayMove(blocks, oldIndex, newIndex));
    }
  };

  const toggleBlock = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setExpandedBlocks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const allIds = blocks.reduce((acc, b) => ({ ...acc, [b.id]: true }), {});
    setExpandedBlocks(allIds);
  };

  const collapseAll = () => {
    setExpandedBlocks({});
  };

  const addBlock = (type: string, index: number) => {
    const newBlock: ContentBlock = {
      id: nanoid(8),
      type,
      title: type === 'custom' ? '' : blockTypes.find((t) => t.id === type)?.label,
      data: getDefaultDataForType(type),
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index, 0, newBlock);
    onChange(newBlocks);
    setExpandedBlocks((prev) => ({ ...prev, [newBlock.id]: true }));
    setShowAddMenu(null);
  };

  const removeBlock = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setBlockToDelete(id);
  };

  const confirmDeleteBlock = () => {
    if (blockToDelete) {
      onChange(blocks.filter((b) => b.id !== blockToDelete));
      setBlockToDelete(null);
    }
  };

  const moveBlock = (index: number, direction: 'up' | 'down', e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === blocks.length - 1) return;

    const newBlocks = [...blocks];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newBlocks[index] as ContentBlock;
    newBlocks[index] = newBlocks[swapIndex] as ContentBlock;
    newBlocks[swapIndex] = temp;
    onChange(newBlocks);
  };

  const updateBlock = (id: string, updates: Partial<ContentBlock>) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  return (
    <div className="flex flex-col">
      {blocks.length > 0 && (
        <div className="flex items-center justify-end gap-2 mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={expandAll}
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
          >
            <Maximize2 size={14} className="mr-1.5" /> Expand All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={collapseAll}
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
          >
            <Minimize2 size={14} className="mr-1.5" /> Collapse All
          </Button>
        </div>
      )}

      {blocks.length === 0 && (
        <div className="text-center py-10 bg-muted/30 border border-dashed rounded-xl flex flex-col items-center">
          <p className="text-muted-foreground text-sm mb-4">No content blocks yet.</p>
          <AddBlockMenu onSelect={(t) => addBlock(t, 0)} />
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map((block, idx) => (
            <SortableBlockWrapper key={block.id} id={block.id}>
              {(attributes, listeners, setNodeRef, style, isDragging) => (
                <div ref={setNodeRef} style={style} className={`group relative ${showAddMenu === idx ? "z-50" : ""}`}>
                  {/* Block Wrapper */}
                  <div
                    className={`bg-card border shadow-sm rounded-xl transition-all overflow-hidden ${
                      isDragging ? 'border-primary/50 shadow-md opacity-80' : 'border-border/60'
                    }`}
                  >
                    {/* Header */}
                    <div
                      className={`bg-muted/20 px-4 py-3 flex items-center justify-between cursor-pointer transition-colors hover:bg-muted/40 ${expandedBlocks[block.id] ? 'border-b border-border/40' : ''}`}
                      onClick={() => toggleBlock(block.id)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-background cursor-grab active:cursor-grabbing"
                          {...attributes}
                          {...listeners}
                        >
                          <GripVertical size={18} />
                        </button>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-transform duration-200 p-1 rounded hover:bg-background"
                          onClick={(e) => toggleBlock(block.id, e)}
                        >
                          {expandedBlocks[block.id] ? (
                            <ChevronDown size={18} />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                        </button>
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-background rounded-md border text-xs font-semibold text-muted-foreground shadow-sm">
                          {blockTypes.find((t) => t.id === block.type)?.icon}
                          {blockTypes.find((t) => t.id === block.type)?.label || block.type}
                        </div>
                        <div className="h-4 w-px bg-border mx-1"></div>
                        <input
                          type="text"
                          placeholder="Section Heading (appears in sidebar)"
                          value={block.title || ''}
                          onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          className="bg-transparent text-sm font-medium focus:outline-none w-64 placeholder:text-muted-foreground/60 focus:bg-background focus:ring-2 focus:ring-primary/20 rounded px-2 py-0.5"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => removeBlock(block.id, e)}
                        className="text-muted-foreground hover:text-destructive p-1.5 rounded-lg hover:bg-destructive/10 transition-colors"
                        title="Remove block"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Content Payload */}
                    {expandedBlocks[block.id] && (
                      <div className="p-4 sm:p-5">
                        <BlockEditor
                          payload={block}
                          onChange={(updates) => updateBlock(block.id, updates)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Inline Add Button between blocks */}
                  <div className={`relative flex items-center justify-center transition-all duration-200 ${showAddMenu === idx ? "opacity-100 z-50 py-6" : "h-6 opacity-0 group-hover:opacity-100 hover:opacity-100"}`}>
                    <div className="absolute inset-0 flex items-center pointer-events-none">
                      <div className="w-full border-t border-dashed border-primary/40"></div>
                    </div>
                    <div className="relative z-10 flex justify-center w-full">
                      {showAddMenu === idx ? (
                        <div className="w-full max-w-2xl px-4">
                          <AddBlockMenu
                            onSelect={(t) => addBlock(t, idx + 1)}
                            onCancel={() => setShowAddMenu(null)}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowAddMenu(idx)}
                          className="bg-background border border-primary/30 text-primary rounded-full p-1 hover:bg-primary/10 transition-colors shadow-sm"
                        >
                          <Plus size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </SortableBlockWrapper>
          ))}
        </SortableContext>
      </DndContext>

      {/* Final Add Button */}
      {blocks.length > 0 && (
        <div className="flex justify-center mt-4">
          {showAddMenu === blocks.length ? (
            <AddBlockMenu
              onSelect={(t) => addBlock(t, blocks.length)}
              onCancel={() => setShowAddMenu(null)}
            />
          ) : (
            <Button
              type="button"
              variant="outline"
              className="rounded-full shadow-sm bg-background border-dashed hover:border-solid hover:bg-primary/5 hover:text-primary transition-all"
              onClick={() => setShowAddMenu(blocks.length)}
            >
              <Plus size={16} className="mr-2" /> Add New Block
            </Button>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!blockToDelete}
        onClose={() => setBlockToDelete(null)}
        onConfirm={confirmDeleteBlock}
        title="Delete Content Block"
        description="Are you sure you want to delete this content block? This action cannot be undone."
      />
    </div>
  );
}

function SortableBlockWrapper({
  id,
  children,
}: {
  id: string;
  children: (
    attributes: any,
    listeners: any,
    setNodeRef: (node: HTMLElement | null) => void,
    style: React.CSSProperties,
    isDragging: boolean
  ) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 50 } : {}),
    position: 'relative' as const,
  };

  return <>{children(attributes, listeners, setNodeRef, style, isDragging)}</>;
}

// ─── Helpers & Sub-Components ──────────────────────────────────────────────


function useStableIds(items: any[]) {
  const idsRef = React.useRef<string[]>([]);
  if (idsRef.current.length !== items.length) {
    // If length changed, append new ids or trim
    if (items.length > idsRef.current.length) {
      const added = items.slice(idsRef.current.length).map(() => nanoid());
      idsRef.current = [...idsRef.current, ...added];
    } else {
      idsRef.current = idsRef.current.slice(0, items.length);
    }
  }
  return {
    ids: idsRef.current,
    setIds: (newIds: string[]) => {
      idsRef.current = newIds;
    }
  };
}

function InnerSortableItem({ id, children, dragHandleProps = {}, className = "" }: { id: string, children: React.ReactNode, dragHandleProps?: any, className?: string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.2, 0, 0, 1)',
    ...(isDragging ? { zIndex: 99, opacity: 0.8 } : { zIndex: 1, opacity: 1 }),
    position: 'relative' as const,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative flex items-start gap-2 transition-all ${
        isDragging ? 'ring-2 ring-primary rounded-lg bg-card shadow-md z-[99]' : ''
      } ${className}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="mt-2.5 cursor-grab active:cursor-grabbing text-muted-foreground opacity-40 hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted touch-none"
        {...dragHandleProps}
      >
        <GripVertical size={14} />
      </div>
      <div className="flex-1 min-w-0 w-full">
         {children}
      </div>
    </div>
  );
}


function LearningPointsEditor({ data, onChange, confirmDelete }: any) {
  const points = Array.isArray(data.points) ? (data.points as string[]) : [];
  const { ids, setIds } = useStableIds(points);
  const [deleteTarget, setDeleteTarget] = useState<{ index: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const newPts = arrayMove(points, oldIndex, newIndex);
      setIds(arrayMove(ids, oldIndex, newIndex)); 
      onChange({ data: { ...data, points: newPts } });
    }
  };

  const hasEmptyPoint = points.some((p: string) => !p || p.trim() === '');
  const emptyCount = points.filter((p: string) => !p || p.trim() === '').length;

  const handleAddPoint = () => {
    if (hasEmptyPoint) return;
    onChange({ data: { ...data, points: [...points, ''] } });
  };

  const handleDeleteClick = (index: number) => {
    const pt = points[index];
    if (!pt || pt.trim() === '') {
      ids.splice(index, 1);
      const newPts = points.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...data, points: newPts } });
    } else {
      setDeleteTarget({ index });
    }
  };

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {points.map((pt, i) => (
              <InnerSortableItem key={ids[i]} id={ids[i] as string} className="group border-b border-border/50 pb-1">
                <div className="flex items-start gap-2 w-full">
                  <span className="mt-2.5 text-blue-500 opacity-70 group-hover:opacity-100 transition-opacity">
                    <List size={16} />
                  </span>
                  <input
                    type="text"
                    className="flex-1 py-2 px-1 text-sm bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none rounded-none transition-colors shadow-none"
                    placeholder="E.g., Learn how to build a scalable architecture..."
                    value={pt}
                    onChange={(e) => {
                      const newPts = [...points];
                      newPts[i] = e.target.value;
                      onChange({ data: { ...data, points: newPts } });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!hasEmptyPoint && pt.trim() !== '') {
                          handleAddPoint();
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(i)}
                    className="p-2 text-muted-foreground hover:text-destructive mt-0.5 transition-colors"
                    title={!pt || !pt.trim() ? "Remove empty point" : "Delete point"}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </InnerSortableItem>
            ))}

            <div className="col-span-full mt-2 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`w-full sm:w-auto transition-all ${
                    hasEmptyPoint ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''
                  }`}
                  disabled={hasEmptyPoint}
                  onClick={handleAddPoint}
                  title={
                    hasEmptyPoint
                      ? 'Please fill in the existing point before adding a new one.'
                      : 'Add a new point'
                  }
                >
                  <Plus size={14} className="mr-1" /> Add Point
                </Button>
                {emptyCount > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const nonEmpty = points.filter((p: string) => p && p.trim() !== '');
                      const newPts = nonEmpty.length > 0 ? nonEmpty : [''];
                      onChange({ data: { ...data, points: newPts } });
                    }}
                    className="text-xs text-muted-foreground hover:text-destructive h-8"
                    title="Remove extra empty points"
                  >
                    <Trash2 size={13} className="mr-1 text-destructive" /> Clear empty points ({emptyCount})
                  </Button>
                )}
              </div>
              {hasEmptyPoint && points.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Please fill in the unfilled point before adding a new one.
                </p>
              )}
            </div>
          </div>
        </SortableContext>
      </DndContext>
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            ids.splice(deleteTarget.index, 1);
            const newPts = points.filter((_: any, idx: number) => idx !== deleteTarget.index);
            onChange({ data: { ...data, points: newPts } });
            setDeleteTarget(null);
          }
        }}
        title="Delete Item"
        description="Are you sure you want to delete this item? This action cannot be undone."
      />
    </div>
  );
}


function SortableItemWrapper({ id, children }: { id: string, children: (props: { setNodeRef: any, style: React.CSSProperties, isDragging: boolean, dragHandleProps: any }) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.2, 0, 0, 1)',
    ...(isDragging ? { zIndex: 99, opacity: 0.8 } : { zIndex: 1, opacity: 1 }),
  };
  return <>{children({ setNodeRef, style, isDragging, dragHandleProps: { ...attributes, ...listeners } })}</>;
}


function ResourcesEditor({ data, onChange, confirmDelete }: any) {
  const items = Array.isArray(data.items) ? data.items : [];
  const { ids, setIds } = useStableIds(items);
  const [deleteTarget, setDeleteTarget] = useState<{ index: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setIds(arrayMove(ids, oldIndex, newIndex));
      onChange({ data: { ...data, items: newItems } });
    }
  };

  const hasEmptyItem = items.some((item: any) => !item.label?.trim() || !item.url?.trim());
  const emptyCount = items.filter((item: any) => !item.label?.trim() && !item.url?.trim()).length;

  const handleAddItem = () => {
    if (hasEmptyItem) return;
    onChange({
      data: {
        ...data,
        items: [...items, { label: '', url: '', type: 'external' }],
      },
    });
  };

  const handleDeleteClick = (index: number) => {
    const item = items[index];
    if (!item.label?.trim() && !item.url?.trim()) {
      ids.splice(index, 1);
      const newItems = items.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...data, items: newItems } });
    } else {
      setDeleteTarget({ index });
    }
  };

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {items.map((res: any, i: number) => (
              <SortableItemWrapper key={ids[i] as string} id={ids[i] as string}>
                {({ setNodeRef, style, isDragging, dragHandleProps }) => (
                  <div
                    ref={setNodeRef}
                    style={style}
                    className={`flex flex-wrap gap-2 py-3 border-b border-border/30 last:border-0 items-start group relative transition-all ${
                      isDragging ? 'opacity-50 ring-2 ring-primary rounded-lg bg-card shadow-md z-50' : ''
                    }`}
                  >
                    <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-muted-foreground opacity-40 hover:opacity-100 transition-opacity p-2 rounded hover:bg-muted mt-0.5 touch-none">
                      <GripVertical size={14} />
                    </div>
                    <input
                      className="flex-1 min-w-[150px] px-3 py-2 text-sm bg-muted/30 hover:bg-muted/50 focus:bg-background border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="Label (e.g., GitHub Repo)"
                      value={res.label || ''}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[i] = { ...newItems[i], label: e.target.value };
                        onChange({ data: { ...data, items: newItems } });
                      }}
                    />
                    <input
                      type="url"
                      className="flex-[2] min-w-[200px] px-3 py-2 text-sm bg-muted/30 hover:bg-muted/50 focus:bg-background border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="https://..."
                      value={res.url || ''}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[i] = { ...newItems[i], url: e.target.value };
                        onChange({ data: { ...data, items: newItems } });
                      }}
                    />
                    <select
                      className="w-[120px] px-3 py-2 text-sm bg-muted/30 hover:bg-muted/50 focus:bg-background border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-primary transition-colors cursor-pointer"
                      value={res.type || 'external'}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[i] = { ...newItems[i], type: e.target.value };
                        onChange({ data: { ...data, items: newItems } });
                      }}
                    >
                      <option value="github">GitHub</option>
                      <option value="repo">Repo</option>
                      <option value="paper">Paper (PDF)</option>
                      <option value="slides">Slides</option>
                      <option value="external">Website</option>
                      <option value="other">Other</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(i)}
                      className="p-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md"
                      title={!res.label?.trim() && !res.url?.trim() ? "Remove empty resource" : "Delete resource"}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </SortableItemWrapper>
            ))}

            <div className="col-span-full mt-2 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={hasEmptyItem ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''}
                  disabled={hasEmptyItem}
                  onClick={handleAddItem}
                  title={hasEmptyItem ? 'Please fill in both label and URL before adding another resource.' : 'Add Resource'}
                >
                  <Plus size={14} className="mr-1" /> Add Resource
                </Button>
                {emptyCount > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const nonEmpty = items.filter((it: any) => it.label?.trim() || it.url?.trim());
                      onChange({ data: { ...data, items: nonEmpty } });
                    }}
                    className="text-xs text-muted-foreground hover:text-destructive h-8"
                  >
                    <Trash2 size={13} className="mr-1 text-destructive" /> Clear empty resources ({emptyCount})
                  </Button>
                )}
              </div>
              {hasEmptyItem && items.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Please provide a label and URL for the current resource before adding another.
                </p>
              )}
            </div>
          </div>
        </SortableContext>
      </DndContext>
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            ids.splice(deleteTarget.index, 1);
            const newItems = items.filter((_: any, idx: number) => idx !== deleteTarget.index);
            onChange({ data: { ...data, items: newItems } });
            setDeleteTarget(null);
          }
        }}
        title="Delete Resource"
        description="Are you sure you want to delete this resource? This action cannot be undone."
      />
    </div>
  );
}


function FaqEditor({ data, onChange, confirmDelete }: any) {
  const questions = Array.isArray(data.questions) ? data.questions : [];
  const { ids, setIds } = useStableIds(questions);
  const [deleteTarget, setDeleteTarget] = useState<{ index: number } | null>(null);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const newQ = arrayMove(questions, oldIndex, newIndex);
      setIds(arrayMove(ids, oldIndex, newIndex));
      onChange({ data: { ...data, questions: newQ } });
    }
  };

  const isItemOpen = (id: string) => openItems[id] !== false; // Default expanded

  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({ ...prev, [id]: !isItemOpen(id) }));
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    ids.forEach((id: string) => {
      next[id] = true;
    });
    setOpenItems(next);
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    ids.forEach((id: string) => {
      next[id] = false;
    });
    setOpenItems(next);
  };

  const hasEmptyFaq = questions.some((q: any) => !q.q?.trim());
  const emptyCount = questions.filter((q: any) => !q.q?.trim() && !q.a?.trim()).length;

  const handleAddFaq = () => {
    if (hasEmptyFaq) return;
    onChange({
      data: {
        ...data,
        questions: [...questions, { q: '', a: '' }],
      },
    });
  };

  const handleDeleteClick = (index: number) => {
    const q = questions[index];
    if (!q.q?.trim() && !q.a?.trim()) {
      ids.splice(index, 1);
      const newQ = questions.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...data, questions: newQ } });
    } else {
      setDeleteTarget({ index });
    }
  };

  return (
    <div className="space-y-3">
      {/* Header action bar */}
      {questions.length > 0 && (
        <div className="flex items-center justify-between pb-1 text-xs text-muted-foreground">
          <span className="font-semibold text-xs tracking-wide">
            {questions.length} FAQ / Accordion Item{questions.length > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={expandAll}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <Maximize2 size={12} className="mr-1" /> Expand All
            </Button>
            <span className="text-border">|</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={collapseAll}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <Minimize2 size={12} className="mr-1" /> Collapse All
            </Button>
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {questions.map((q: any, i: number) => {
              const id = ids[i] as string;
              const open = isItemOpen(id);

              return (
                <SortableItemWrapper key={id} id={id}>
                  {({ setNodeRef, style, isDragging, dragHandleProps }) => (
                    <div
                      ref={setNodeRef}
                      style={style}
                      className={`border border-border/60 rounded-lg bg-card shadow-sm hover:border-border transition-all overflow-hidden ${
                        isDragging ? 'ring-2 ring-primary shadow-xl z-[99] bg-background/95 backdrop-blur' : ''
                      }`}
                    >
                      {/* Accordion Item Header Bar */}
                      <div
                        onClick={() => toggleItem(id)}
                        className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer select-none transition-colors ${
                          open ? 'bg-muted/20 border-b border-border/40' : 'hover:bg-muted/20'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div
                            {...dragHandleProps}
                            onClick={(e) => e.stopPropagation()}
                            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors touch-none shrink-0"
                            title="Drag to reorder accordion item"
                          >
                            <GripVertical size={16} />
                          </div>

                          <span className="bg-primary/10 text-primary font-bold text-xs px-2.5 py-0.5 rounded-md shrink-0">
                            Q{i + 1}
                          </span>

                          <span
                            className={`text-sm truncate font-medium ${
                              q.q?.trim() ? 'text-foreground' : 'italic text-muted-foreground'
                            }`}
                          >
                            {q.q?.trim() || 'Untitled Question'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(i)}
                            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                            title={!q.q?.trim() && !q.a?.trim() ? 'Remove empty FAQ' : 'Delete FAQ item'}
                          >
                            <Trash2 size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleItem(id)}
                            className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                            title={open ? 'Collapse' : 'Expand'}
                          >
                            <ChevronDown
                              size={16}
                              className={`transition-transform duration-200 ${open ? 'rotate-180 text-primary' : ''}`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Accordion Item Body (Inputs) */}
                      {open && (
                        <div className="p-3 space-y-3 bg-background animate-in fade-in-50 duration-150">
                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                              Question
                            </label>
                            <Input
                              value={q.q || ''}
                              onChange={(e) => {
                                const newQ = [...questions];
                                newQ[i] = { ...newQ[i], q: e.target.value };
                                onChange({ data: { ...data, questions: newQ } });
                              }}
                              placeholder="e.g., What will I learn in this session?"
                              className="font-medium text-sm bg-background border-border/80 focus:border-primary"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                              Answer
                            </label>
                            <RichTextEditor
                              value={q.a || ''}
                              onChange={(val) => {
                                const newQ = [...questions];
                                newQ[i] = { ...newQ[i], a: val };
                                onChange({ data: { ...data, questions: newQ } });
                              }}
                              placeholder="Provide a detailed answer or explanation..."
                              minHeight={150}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </SortableItemWrapper>
              );
            })}

            <div className="pt-1 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={hasEmptyFaq ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''}
                  disabled={hasEmptyFaq}
                  onClick={handleAddFaq}
                  title={hasEmptyFaq ? 'Please fill in the question before adding another.' : 'Add Q&A'}
                >
                  <Plus size={14} className="mr-1" /> Add Q&A
                </Button>
                {emptyCount > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const nonEmpty = questions.filter((it: any) => it.q?.trim() || it.a?.trim());
                      onChange({ data: { ...data, questions: nonEmpty.length > 0 ? nonEmpty : [{ q: '', a: '' }] } });
                    }}
                    className="text-xs text-muted-foreground hover:text-destructive h-8"
                  >
                    <Trash2 size={13} className="mr-1 text-destructive" /> Clear empty FAQs ({emptyCount})
                  </Button>
                )}
              </div>
              {hasEmptyFaq && questions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Please fill in the question before adding another.
                </p>
              )}
            </div>
          </div>
        </SortableContext>
      </DndContext>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            ids.splice(deleteTarget.index, 1);
            const newQ = questions.filter((_: any, idx: number) => idx !== deleteTarget.index);
            onChange({ data: { ...data, questions: newQ } });
            setDeleteTarget(null);
          }
        }}
        title="Delete FAQ Item"
        description="Are you sure you want to delete this FAQ item? This action cannot be undone."
      />
    </div>
  );
}


function TimelineEditor({ data, onChange, confirmDelete }: any) {
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const { ids, setIds } = useStableIds(steps);
  const [deleteTarget, setDeleteTarget] = useState<{ index: number } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const newSteps = arrayMove(steps, oldIndex, newIndex);
      setIds(arrayMove(ids, oldIndex, newIndex));
      onChange({ data: { ...data, steps: newSteps } });
    }
  };

  const hasEmptyStep = steps.some((s: any) => !s.title?.trim());
  const emptyCount = steps.filter((s: any) => !s.title?.trim() && !s.description?.trim()).length;

  const handleAddStep = () => {
    if (hasEmptyStep) return;
    onChange({
      data: {
        ...data,
        steps: [...steps, { title: '', description: '' }],
      },
    });
  };

  const handleDeleteClick = (index: number) => {
    const s = steps[index];
    if (!s.title?.trim() && !s.description?.trim()) {
      ids.splice(index, 1);
      const newSteps = steps.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...data, steps: newSteps } });
    } else {
      setDeleteTarget({ index });
    }
  };

  return (
    <div className="space-y-6 relative pt-2">
      <div className="absolute left-[19px] top-4 bottom-4 w-px bg-border/60 hidden md:block z-0"></div>
      
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {steps.map((step: any, i: number) => (
            <SortableItemWrapper key={ids[i] as string} id={ids[i] as string}>
              {({ setNodeRef, style, isDragging, dragHandleProps }) => (
                <div
                  ref={setNodeRef}
                  style={style}
                  className={`relative flex flex-col md:flex-row gap-4 md:pl-12 group pb-4 border-b border-border/30 last:border-0 last:pb-0 ${isDragging ? 'opacity-50 ring-2 ring-primary rounded-xl bg-background/50 backdrop-blur' : ''}`}
                >
                  <div className="hidden md:flex absolute left-0 top-3 w-10 h-10 -ml-[1px] items-center justify-center z-10">
                    <div className="w-8 h-8 rounded-full border-4 border-background bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold shadow-sm transition-colors relative">
                      <div {...dragHandleProps} className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 hover:opacity-100 cursor-grab active:cursor-grabbing bg-primary text-primary-foreground transition-opacity touch-none">
                         <GripVertical size={14} />
                      </div>
                      <span className="group-hover:opacity-0 transition-opacity">{i + 1}</span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-3 relative group/inner p-2 -mx-2 hover:bg-muted/10 rounded-lg transition-colors z-10">
                    <input
                      className="w-full py-1.5 px-0 text-sm border-0 border-b border-transparent bg-transparent font-medium hover:border-border focus:border-primary focus:outline-none transition-colors shadow-none pr-10"
                      placeholder="Step Title"
                      value={step.title || ''}
                      onChange={(e) => {
                        const newS = [...steps];
                        newS[i] = { ...newS[i], title: e.target.value };
                        onChange({ data: { ...data, steps: newS } });
                      }}
                    />
                    <div className="pt-1">
                      <RichTextEditor
                        value={step.description || ''}
                        onChange={(val) => {
                          const newS = [...steps];
                          newS[i] = { ...newS[i], description: val };
                          onChange({ data: { ...data, steps: newS } });
                        }}
                        placeholder="Step Description"
                        minHeight={150}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(i)}
                      className="absolute top-1 right-2 p-1.5 opacity-0 group-hover/inner:opacity-100 transition-opacity text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md"
                      title={!step.title?.trim() && !step.description?.trim() ? "Remove empty step" : "Delete step"}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </SortableItemWrapper>
          ))}
        </SortableContext>
      </DndContext>

      <div className="md:pl-12 pt-2 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={hasEmptyStep ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''}
            disabled={hasEmptyStep}
            onClick={handleAddStep}
            title={hasEmptyStep ? 'Please fill in the title of the current step before adding another.' : 'Add Step'}
          >
            <Plus size={14} className="mr-1" /> Add Step
          </Button>
          {emptyCount > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const nonEmpty = steps.filter((it: any) => it.title?.trim() || it.description?.trim());
                onChange({ data: { ...data, steps: nonEmpty.length > 0 ? nonEmpty : [{ title: '', description: '' }] } });
              }}
              className="text-xs text-muted-foreground hover:text-destructive h-8"
            >
              <Trash2 size={13} className="mr-1 text-destructive" /> Clear empty steps ({emptyCount})
            </Button>
          )}
        </div>
        {hasEmptyStep && steps.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Please fill in the title of the current step before adding another.
          </p>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            ids.splice(deleteTarget.index, 1);
            const newSteps = steps.filter((_: any, idx: number) => idx !== deleteTarget.index);
            onChange({ data: { ...data, steps: newSteps } });
            setDeleteTarget(null);
          }
        }}
        title="Delete Timeline Step"
        description="Are you sure you want to delete this step? This action cannot be undone."
      />
    </div>
  );
}

function getDefaultDataForType(type: string) {
  switch (type) {
    case 'learning-points':
      return { points: [''] };
    case 'gallery':
      return { images: [] };
    case 'resources':
      return { items: [] };
    case 'markdown':
    case 'custom':
      return { text: '' };
    case 'faq':
      return { questions: [{ q: '', a: '' }] };
    case 'callout':
      return { style: 'info', text: '', title: '' };
    case 'code':
      return { language: 'typescript', code: '' };
    case 'timeline':
      return { steps: [{ title: '', description: '' }] };
    case 'overview':
    case 'deep-dive':
    default:
      return { text: '' };
  }
}

function AddBlockMenu({
  onSelect,
  onCancel,
}: {
  onSelect: (type: string) => void;
  onCancel?: () => void;
}) {
  return (
    <div className="inline-block relative w-full max-w-2xl">
      <div className="bg-background border border-border/80 shadow-2xl rounded-2xl p-4 z-20 w-full animate-in fade-in zoom-in-95 duration-200">
        {onCancel && (
          <div className="flex justify-between items-center mb-3 pb-2 border-b border-border/50">
            <span className="text-sm font-semibold">Select Content Block</span>
            <button
              type="button"
              onClick={onCancel}
              className="text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted p-1 rounded-md transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {blockTypes.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t.id)}
              className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted/60 hover:shadow-sm border border-transparent hover:border-border/50 transition-all text-left group"
            >
              <div className="text-primary mt-0.5 bg-primary/10 p-1.5 rounded-lg group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                {t.icon}
              </div>
              <div className="flex flex-col">
                <span className="text-foreground text-sm font-semibold leading-tight">
                  {t.label}
                </span>
                <span className="text-[10px] text-muted-foreground font-normal leading-tight mt-1">
                  {t.desc}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Individual Block Editors ──────────────────────────────────────────────

function BlockEditor({
  payload,
  onChange,
}: {
  payload: ContentBlock;
  onChange: (updates: Partial<ContentBlock>) => void;
}) {
  const t = payload.type;
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; index: number } | null>(null);

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { type, index } = deleteTarget;
    if (type === 'point' && t === 'learning-points') {
      const newPts = (payload.data.points as string[]).filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...payload.data, points: newPts } });
    } else if (type === 'resource' && t === 'resources') {
      const newItems = payload.data.items.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...payload.data, items: newItems } });
    } else if (type === 'faq' && t === 'faq') {
      const newQ = payload.data.questions.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...payload.data, questions: newQ } });
    } else if (type === 'timeline' && t === 'timeline') {
      const newS = payload.data.steps.filter((_: any, idx: number) => idx !== index);
      onChange({ data: { ...payload.data, steps: newS } });
    }
    setDeleteTarget(null);
  };

  if (t === 'overview') {
    return (
      <RichTextEditor
        value={payload.data.text || ''}
        onChange={(val) => onChange({ data: { ...payload.data, text: val } })}
        placeholder="Short summary text (plain text, a few sentences)"
        minHeight={150}
      />
    );
  }

  if (t === 'deep-dive' || t === 'markdown' || t === 'custom') {
    return (
      <div className="flex flex-col">
        <RichTextEditor
          value={payload.data.text || ''}
          onChange={(val) => onChange({ data: { ...payload.data, text: val } })}
          placeholder="Start writing your content here..."
          height={400}
        />
        <p className="text-[10px] text-muted-foreground px-3 py-1.5 bg-muted/30 border-x border-b border-border rounded-b-lg -mt-1">
          Use the toolbar above to format text, add tables, lists, and images. What you see is what
          you get!
        </p>
      </div>
    );
  }

  if (t === 'learning-points') {
    return <LearningPointsEditor data={payload.data} onChange={onChange} confirmDelete={confirmDelete} />;
  }
  if (t === 'resources') {
    return <ResourcesEditor data={payload.data} onChange={onChange} confirmDelete={confirmDelete} />;
  }  if (t === 'gallery') {
    return <GalleryBlockEditor payload={payload} onChange={onChange} />;
  }

  if (t === 'faq') {
    return <FaqEditor data={payload.data} onChange={onChange} confirmDelete={confirmDelete} />;
  }  if (t === 'callout') {
    return (
      <div className="space-y-3 border rounded-xl p-4 bg-muted/10">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold mb-1 text-muted-foreground">Style</label>
            <select
              className="w-full p-2 text-sm border rounded-lg bg-background"
              value={payload.data.style || 'info'}
              onChange={(e) => onChange({ data: { ...payload.data, style: e.target.value } })}
            >
              <option value="info">Info (Blue)</option>
              <option value="warning">Warning (Amber)</option>
              <option value="success">Success (Green)</option>
              <option value="quote">Quote</option>
            </select>
          </div>
          <div className="flex-[3]">
            <label className="block text-xs font-semibold mb-1 text-muted-foreground">
              Optional Header / Author
            </label>
            <input
              className="w-full p-2 text-sm border rounded-lg bg-background"
              placeholder="e.g. Note, or John Doe"
              value={payload.data.title || ''}
              onChange={(e) => onChange({ data: { ...payload.data, title: e.target.value } })}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground">Content</label>
          <textarea
            className="w-full p-2 text-sm border rounded-lg bg-background h-24 resize-y"
            placeholder="Enter the callout text..."
            value={payload.data.text || ''}
            onChange={(e) => onChange({ data: { ...payload.data, text: e.target.value } })}
          />
        </div>
      </div>
    );
  }

  if (t === 'code') {
    return (
      <div className="space-y-3 border rounded-xl p-4 bg-slate-900">
        <div className="flex gap-4 items-center">
          <label className="block text-xs font-semibold text-slate-400">Language:</label>
          <input
            className="w-48 p-1.5 text-sm border border-slate-700 rounded-lg bg-slate-800 text-slate-200"
            placeholder="e.g. typescript, python, bash"
            value={payload.data.language || ''}
            onChange={(e) => onChange({ data: { ...payload.data, language: e.target.value } })}
          />
        </div>
        <textarea
          className="w-full p-3 text-sm border border-slate-700 rounded-lg bg-slate-950 text-slate-200 font-mono h-48 resize-y"
          placeholder="Paste your code here..."
          value={payload.data.code || ''}
          onChange={(e) => onChange({ data: { ...payload.data, code: e.target.value } })}
        />
      </div>
    );
  }

  if (t === 'timeline') {
    return <TimelineEditor data={payload.data} onChange={onChange} confirmDelete={confirmDelete} />;
  }
  return <p className="text-xs text-muted-foreground">Unknown block type: {t}</p>;
}

// ─── Gallery Block Sub-editor ─────────────────────────────────────────────
// (Brings in the Upload / URL toggle logic per image)

function GalleryBlockEditor({
  payload,
  onChange,
}: {
  payload: ContentBlock;
  onChange: (updates: Partial<ContentBlock>) => void;
}) {
  const [uploadFile] = useUploadFileMutation();
  const images = Array.isArray(payload.data.images) ? payload.data.images : [];

  const [modes, setModes] = useState<Record<number, 'url' | 'file'>>({});
  const [uploadingIdx, setUploadingIdx] = useState<Record<number, boolean>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});
  const fileRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const confirmDelete = () => {
    if (deleteTarget === null) return;
    const newImgs = images.filter((_img: any, idx: number) => idx !== deleteTarget);
    onChange({ data: { ...payload.data, images: newImgs } });
    setDeleteTarget(null);
  };

  const setMode = (i: number, m: 'url' | 'file') => setModes((prev) => ({ ...prev, [i]: m }));

  const updateImg = (i: number, field: string, val: string) => {
    const newImgs = [...images];
    newImgs[i] = { ...newImgs[i], [field]: val };
    onChange({ data: { ...payload.data, images: newImgs } });
  };

  const hasEmptyImg = images.some((img: any) => !img.url || !img.url.trim());

  const addImg = () => {
    if (images.length >= 10 || hasEmptyImg) return;
    onChange({ data: { ...payload.data, images: [...images, { url: '', caption: '' }] } });
  };

  const removeImg = (i: number) => {
    if (!images[i]?.url?.trim()) {
      ids.splice(i, 1);
      const newImgs = images.filter((_img: any, idx: number) => idx !== i);
      onChange({ data: { ...payload.data, images: newImgs } });
    } else {
      setDeleteTarget(i);
    }
  };

  const { ids, setIds } = useStableIds(images);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      const newImages = arrayMove(images, oldIndex, newIndex);
      setIds(arrayMove(ids, oldIndex, newIndex));
      onChange({ data: { ...payload.data, images: newImages } });
    }
  };

  const handleFileChange = async (i: number, file: File | null) => {
    if (!file) return;
    setUploadingIdx((p) => ({ ...p, [i]: true }));
    setUploadErrors((p) => ({ ...p, [i]: '' }));
    try {
      const result = await uploadFile(file).unwrap();
      updateImg(i, 'url', result.url);
      setMode(i, 'url'); // switch mode on success
    } catch (err: any) {
      setUploadErrors((p) => ({
        ...p,
        [i]: err?.data?.message || err?.message || 'Upload failed',
      }));
    } finally {
      setUploadingIdx((p) => {
        const n = { ...p };
        delete n[i];
        return n;
      });
    }
  };

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {images.map((img: any, i: number) => {
              const mode = modes[i] ?? 'url';
              const isUploading = !!uploadingIdx[i];
              const uploadError = uploadErrors[i];

              return (
                <SortableItemWrapper key={ids[i] as string} id={ids[i] as string}>
                  {({ setNodeRef, style, isDragging, dragHandleProps }) => (
                    <div
                      ref={setNodeRef}
                      style={style}
                      className={`border rounded-lg overflow-hidden bg-card shadow-sm flex flex-col group relative ${isDragging ? 'ring-2 ring-primary shadow-lg z-[99]' : ''}`}
                    >
                      <div className="absolute top-2 left-2 z-10">
                        <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing p-1.5 bg-background/90 text-muted-foreground hover:text-foreground rounded shadow-sm border backdrop-blur">
                          <GripVertical size={14} />
                        </div>
                      </div>

                      {/* Header Image Preview */}
                      <div className="relative w-full h-40 bg-muted/30 flex items-center justify-center border-b overflow-hidden">
                        {img.url ? (
                          <>
                            <img
                              src={img.url}
                              alt="Preview"
                              className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-300"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.nextElementSibling) {
                                  e.currentTarget.nextElementSibling.classList.remove('hidden');
                                }
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center hidden bg-muted/50">
                              <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                            </div>
                          </>
                        ) : (
                          <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                        )}

                        {/* Image Number Badge */}
                        <div className="absolute top-2 left-10 pointer-events-none">
                          <span className="bg-background/90 backdrop-blur-md px-2 py-1 rounded-md text-[10px] font-semibold shadow-sm border text-foreground/80">
                            Image {i + 1}
                          </span>
                        </div>

                        {/* Delete Button */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => removeImg(i)}
                            className="bg-background/90 backdrop-blur-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-1.5 rounded-lg shadow-sm border transition-colors"
                            title="Delete image"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Controls Section */}
                      <div className="p-3 space-y-3 flex-1 flex flex-col justify-end bg-muted/5">
                        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 text-[10px] w-fit">
                          <button
                            type="button"
                            onClick={() => setMode(i, 'file')}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all ${mode === 'file' ? 'bg-background text-foreground shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                          >
                            <Upload size={11} /> File
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode(i, 'url')}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all ${mode === 'url' ? 'bg-background text-foreground shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                          >
                            <Globe size={11} /> URL
                          </button>
                        </div>

                        {mode === 'file' && (
                          <div>
                            <input
                              ref={(el) => {
                                fileRefs.current[i] = el;
                              }}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleFileChange(i, e.target.files?.[0] ?? null)}
                            />
                            <button
                              type="button"
                              disabled={isUploading}
                              onClick={() => fileRefs.current[i]?.click()}
                              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg py-2.5 text-muted-foreground hover:text-primary transition-colors disabled:opacity-60 bg-background text-xs font-medium"
                            >
                              {isUploading ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload size={14} />
                                  Choose Image
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {mode === 'url' && (
                          <div className="space-y-1">
                            <div className="relative">
                              <Globe className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                              <Input
                                placeholder="https://..."
                                value={img.url}
                                onChange={(e) => updateImg(i, 'url', e.target.value)}
                                className="pl-8 text-xs h-8"
                              />
                            </div>
                            {img.url && getUrlWarning(img.url) && (
                              <p className="text-[10px] text-amber-600 font-medium px-1">
                                {getUrlWarning(img.url)}
                              </p>
                            )}
                          </div>
                        )}

                        {uploadError && <p className="text-[10px] text-destructive px-1">{uploadError}</p>}

                        <div className="pt-1">
                          <Input
                            placeholder="Caption (optional)"
                            value={img.caption || ''}
                            onChange={(e) => updateImg(i, 'caption', e.target.value)}
                            className="text-xs h-8 bg-background"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </SortableItemWrapper>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addImg}
          disabled={images.length >= 10 || hasEmptyImg}
          className={hasEmptyImg ? 'opacity-50 cursor-not-allowed bg-muted/30' : ''}
          title={hasEmptyImg ? 'Please provide an image for the current slot before adding another.' : 'Add Image'}
        >
          <Plus size={14} className="mr-1" /> Add Image
        </Button>
        {images.length >= 10 && (
          <span className="text-xs text-amber-600 font-medium">
            Maximum 10 images allowed per gallery block.
          </span>
        )}
        {hasEmptyImg && images.length < 10 && (
          <span className="text-xs text-muted-foreground">
            Please provide an image for the current slot before adding another.
          </span>
        )}
      </div>
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
      />
    </div>
  );
}
